import React, { useEffect, useRef, useState } from 'react';
import { getCroppedImageUrl } from '@/components/common/CroppedImage';
import { CroppedImage } from '@weee-fe/module';
import Constants from '@/constants/const';
import Swiper, { SwiperSlide, SwiperTypes } from '@/components/common/Swiper';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from 'tracks/useImpressionT2';
import useClickActionT2 from 'tracks/useClickActionT2';
import { CmsComponentProps } from '@/types/cms';
import { getCookie } from '@/utils/cookie';
import { getHomePageDataByUrl } from '@/api/content';
import PLACEHOLDER_LOGO from '@/assets/images/logo/weee-placeholder-logo.png';
import LazyImage from '@/components/common/LazyLoad/image-lazy';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import WeeeVideo, { WeeeVideoRefProps } from '@/components/pdp/Picture/video';
import { BannerType } from '@/constants/track';
import styles from './carousel-v3.module.scss';
import { handleCmsJump } from '../../tools/cms-link';
import { useRouter } from 'next/router';
import { getBannerLoopInterval } from '@/utils/get-banner-loop-interval';

const CarouselBanner: React.FC<CmsComponentProps> = props => {
  let {
    data: info,
    componentPos,
    properties,
    skeleton,
    trackingContext,
    isStatic,
    postUrl,
    componentDataSourceKey,
    cmsComponentKey
  } = props;
  const eventKey = properties?.event_key || cmsComponentKey;
  const videoRefList = useRef([]);
  const previousVideoRef = useRef<WeeeVideoRefProps>(null);
  const prevVideoIndex = useRef(null);
  const videoInfoMap = useRef({});
  const currSwiperRef = useRef<any>(null);
  const router = useRouter();
  const [load, setLoad] = useState(true);
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const [data, setData] = useState(info);
  const dataList = data?.banner_list || data?.carousel || [];

  const onSlideChange = async (swiper: SwiperTypes) => {
    currSwiperRef.current = swiper;
    const { realIndex, activeIndex, slides } = swiper;

    let currItem = dataList[realIndex] || {};
    let video = slides?.[activeIndex]?.querySelector('video');

    if (currItem?.media_type === 'video' && !video) return;
    swiper.slides.forEach(slide => {
      let el: HTMLDivElement = slide.querySelector('.custom-pagination');
      el?.offsetWidth && slide.setAttribute('style', `--pagination-width:${el.offsetWidth || 0}px`);
    });

    // 被swiper复制的video ref找到当前选项的video ref
    let currentRef = videoRefList.current.find(item => item.index === realIndex && item.ref.video === video);
    let videoInfo = videoInfoMap.current[realIndex];
    // 非当前视频暂停
    for (let idx in videoInfoMap.current) {
      let item = videoInfoMap.current[idx];
      if (item.status === 'play' && Number(idx) != realIndex) {
        item.ref.pauseVideo();
        previousVideoRef.current?.pauseVideo();
      }
    }
    // 第一次自动播放视频
    if (currItem?.media_type === 'video' && currItem.autoplay !== 0 && videoInfo?.status === 'initial') {
      previousVideoRef.current?.pauseVideo();
      currentRef.ref?.autoPlayVideo();
    } else if (
      //继续播放
      currItem?.media_type === 'video' &&
      (videoInfo?.status === 'pause' || videoInfo?.status === 'play')
    ) {
      // 为了兼容swiper的第一个item一碰元素就变的问题
      previousVideoRef.current?.pauseVideo();
      //继续播放视频
      const { currentTime, muted } = videoInfo;
      video.currentTime = currentTime || previousVideoRef.current?.video?.currentTime || 0;
      video.muted = muted ?? previousVideoRef.current?.video?.muted ?? false;
      let newVideo = currentRef.ref.video;
      if (newVideo && newVideo !== video) {
        currentRef.ref.parentElement.removeChild(newVideo);
        currentRef.ref.parentElement.appendChild(video);
      } else {
        if (newVideo !== video) {
          currentRef.ref.parentElement.appendChild(video);
        }
      }
      currentRef.ref.playVideo();
      previousVideoRef.current = currentRef.ref;
    } else if (previousVideoRef.current) {
      previousVideoRef.current.pauseVideo();
    }
  };

  const bindVideoRefs = (currRef: WeeeVideoRefProps, i: number, item: any) => {
    if (!currRef) return;
    let videoParentEl = currRef.video;
    let currVideo;
    if (videoParentEl && !videoRefList.current.some(child => child?.ref?.parentElement === currRef.parentElement)) {
      currVideo = {
        ref: currRef,
        index: i
      };
      videoRefList.current.push(currVideo);
    }
    if (!currVideo) return;

    // let events = ['play', 'pause', 'ended', 'canplay'];
    if (!videoInfoMap.current[i]) {
      videoInfoMap.current[i] = {
        data: item,
        ref: currRef,
        status: 'initial'
      };
    }
    const onPlay = () => {
      videoInfoMap.current[i].status = 'play';
      videoInfoMap.current[i].ref = currRef;
      previousVideoRef.current = currRef;
      prevVideoIndex.current = i;

      videoRefList.current.forEach(child => {
        if (child.index === i) {
          child.ref.setShowPlayIcon(false);
          child.ref.setShowMuteIcon(true);
        }
      });
    };
    const onEnded = () => {
      videoInfoMap.current[i].status = 'ended';

      if (item.autoplay !== -1) {
        videoRefList.current.forEach(child => {
          if (child.index === i) {
            child.ref.video.currentTime = 0;
            child.ref.setShowPoster(true);
            child.ref.setShowMuteIcon(false);
            child.ref.setShowPlayIcon(true);
          }
        });
        previousVideoRef.current = null;
        videoInfoMap.current[i].ref = null;
      }
    };

    const onPause = event => {
      let info = videoInfoMap.current[i];
      info.status = 'pause';
      info.currentTime = event.target.currentTime;
      info.muted = event.target.muted;
    };
    // 视频能播放时显示静音icon，并且通知被swiper复制的视频
    // const onCanPlay = () => {
    //   videoRefList.current.forEach(child => {
    //     if (child.index === i) {
    //       child.ref.setShowMuteIcon(true);
    //     }
    //   });
    // };
    // 静音 时通知状态到呗复制的视频
    const onVolumeChange = (event, isMuted) => {
      let info = videoInfoMap.current[i];
      info.muted = event.target.muted;
      videoRefList.current.forEach(child => {
        if (child.index === i) {
          child.ref.setIsMuted(isMuted);
        }
      });
    };
    currRef.events.on('play', onPlay);
    currRef.events.on('pause', onPause);
    currRef.events.on('ended', onEnded);
    // currRef.events.on('canplay', onCanPlay);
    currRef.events.on('volumechange', onVolumeChange);
  };

  const fetchData = async () => {
    const zipcode = getCookie(Constants.ZIP_CODE);
    const lang = getCookie(Constants.LANGUAGE);
    const date = getCookie(Constants.DELIVERY_DATE);
    const res = await getHomePageDataByUrl(postUrl, {
      zipcode,
      lang,
      date,
      dataobject_key: componentDataSourceKey
    });
    setLoad(false);
    if (res?.result) {
      setData(res.object);
    }
  };

  useEffect(() => {
    if (!isStatic) {
      fetchData();
    } else {
      setLoad(false);
    }
  }, []);

  const handleOnScroll = () => {
    if (!currSwiperRef.current) return;
    let i = currSwiperRef.current.realIndex;
    let item = dataList[i];
    if (item?.media_type !== 'video') return;
    let inView = window.scrollY <= currSwiperRef.current.el.offsetTop + currSwiperRef.current.el.offsetHeight;
    if (!inView && previousVideoRef.current) {
      if (window.scrollY > 300) {
        previousVideoRef.current.pauseVideo();
        let timer = setTimeout(() => {
          videoInfoMap.current[i].status = 'hidden';
          clearTimeout(timer);
        });
      }
    } else {
      let status = videoInfoMap.current[i]?.status;
      if (inView && status === 'hidden') {
        previousVideoRef.current?.playVideo();
      }
    }
  };

  useEffect(() => {
    if (dataList?.length === 0) return;
    if (!dataList?.some(item => item.media_type === 'video')) return;
    // 当前视频往下滑动时隐藏
    document.addEventListener('scroll', handleOnScroll);
    return () => {
      document.removeEventListener('scroll', handleOnScroll);
    };
  }, [dataList]);

  if (load) {
    return skeleton;
  }

  if (dataList?.length) {
    const interval = getBannerLoopInterval(data?.loop_interval ?? properties?.loop_interval);
    return (
      <div data-module={props.cmsComponentKey} data-testid="mod-main-banner" className="px-5 mt-3" id="bannerWrap">
        <div className="rounded-[10px] relative h-[35.645vw] min-h-[133px]">
          {dataList?.length === 1 ? (
            <ImpTracker
              impTrackingT2={() => {
                //如果有链接才上报曝光 by: tina
                if (dataList[0]?.detail || dataList[0]?.link_url) {
                  bannerImpression({
                    mod_nm: eventKey,
                    mod_pos: componentPos,
                    co: {
                      banner_pos: 0,
                      url: dataList[0]?.detail || dataList[0]?.link_url,
                      banner_type: BannerType.homeMainBanner,
                      banner_id: `${dataList[0]?.id || ''}`
                    },
                    ctx: trackingContext
                  });
                }
              }}
              clickTrackingT2={() => {
                if (dataList[0]?.detail || dataList[0]?.link_url) {
                  bannerClick({
                    mod_nm: eventKey,
                    mod_pos: componentPos,
                    co: {
                      banner_pos: 0,
                      url: dataList[0]?.detail || dataList[0]?.link_url,
                      banner_type: BannerType.homeMainBanner,
                      banner_id: `${dataList[0]?.id || ''}`
                    },
                    ctx: trackingContext
                  });
                }
              }}
              className="h-full">
              <div
                role="button"
                data-testid="wid-main-banner-card"
                onClick={e => {
                  const linkUrl = dataList[0].detail || dataList[0].link_url;
                  if (linkUrl) {
                    handleCmsJump(linkUrl, router);
                  }
                }}
                className="block h-full w-full rounded-[10px]"
                aria-label={`Carousel banner 0`}>
                <LazyImage
                  src={getCroppedImageUrl(dataList[0].img_url, { renderWidth: 375 })}
                  className="block h-full w-full rounded-[10px] object-cover"
                  placeholder={
                    <div className={twMerge('flex justify-center items-center h-full w-full rounded-[10px]')}>
                      <div
                        className="w-full h-full"
                        style={{
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          backgroundImage: `url(${PLACEHOLDER_LOGO})`,
                          backgroundSize: '105px 31.5px'
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </ImpTracker>
          ) : (
            <Swiper
              onInit={onSlideChange}
              spaceBetween={20}
              loop={data?.loop ?? properties?.scroll === 'auto'}
              speed={500}
              pagination={{
                dynamicBullets: true,
                dynamicMainBullets: 3
              }}
              slidesPerView={1}
              onSlideChange={onSlideChange}
              className={twMerge('rounded-[10px] h-full', styles.swiper)}
              autoplay={
                data?.autoplay ?? (properties?.autoplay === 'yes' || properties?.scroll === 'auto')
                  ? {
                      delay: interval,
                      disableOnInteraction: false
                    }
                  : false
              }>
              {dataList?.map((item, i) => {
                const moreLink = item.detail || item.link_url;
                const bannerTrackParams = {
                  mod_nm: eventKey,
                  mod_pos: componentPos,
                  co: {
                    banner_pos: i,
                    url: moreLink,
                    banner_type: BannerType.homeMainBanner,
                    banner_id: `${item.id || ''}`
                  },
                  ctx: trackingContext
                };
                return (
                  <SwiperSlide key={`banner-${i}`}>
                    <ImpTracker
                      impTrackingT2={() => {
                        //如果有链接才上报曝光 by: tina
                        if (moreLink) {
                          bannerImpression(bannerTrackParams);
                        }
                      }}
                      className="h-full">
                      {moreLink ? (
                        <div
                          role="button"
                          aria-label={`Carousel banner ${i}`}
                          data-testid={`wid-main-banner-card-${i}`}
                          className="block h-full"
                          onClick={e => {
                            e.stopPropagation();
                            if (moreLink) {
                              bannerClick(bannerTrackParams);
                              handleCmsJump(moreLink, router);
                            }
                          }}>
                          {item?.media_type === 'video' ? (
                            <WeeeVideo
                              autoPlay={false}
                              animation={false}
                              loop={item.autoplay === -1}
                              poster={getCroppedImageUrl(item.img_url, { renderWidth: 375, devicePixelRatio: 2 })}
                              className={classNames('box-border w-full h-full')}
                              posterClassName="w-full h-full object-cover"
                              muteClassName="w-8 h-8 right-1 bottom-1"
                              videoClassName="w-full h-full absolute rounded object-cover"
                              controlPanel={{ showPlayIcon: true, showMuteIcon: true }}
                              defaultProps={{ showPlayIcon: item.autoplay === 0, showMuteIcon: false }}
                              src={item.media_url}
                              ref={ref => bindVideoRefs(ref, i, item)}
                              onPlayIconClick={e => e.preventDefault()}
                              muteIconUrl={require('@/components/pdp/Picture/video/images/home-muted.svg')}
                              soundIconUrl={require('@/components/pdp/Picture/video/images/home-sound.svg')}
                            />
                          ) : (
                            <CroppedImage
                              className={classNames('box-border block h-full w-full rounded-[10px] object-cover')}
                              wrapperClassName={classNames('h-full w-full')}
                              url={item.img_url}
                              alt={`weee carousel v3 component ${i} more link`}
                              configs={{
                                renderWidth: 375
                              }}
                              placeholder={
                                <div className={twMerge('flex justify-center items-center h-full box-border')}>
                                  <div
                                    className="w-full h-full"
                                    style={{
                                      backgroundRepeat: 'no-repeat',
                                      backgroundPosition: 'center',
                                      backgroundImage: `url(${PLACEHOLDER_LOGO})`,
                                      backgroundSize: '105px 31.5px'
                                    }}
                                  />
                                </div>
                              }
                            />
                          )}
                        </div>
                      ) : (
                        <div className="h-full" data-testid={`wid-main-banner-card-${i}`}>
                          <CroppedImage
                            className={classNames('box-border block h-full w-full rounded-[10px] object-cover')}
                            wrapperClassName={classNames('box-border h-full w-full')}
                            url={item.img_url}
                            configs={{
                              renderWidth: 375
                            }}
                            alt={`weee carousel v3 component ${i} more link`}
                            placeholder={
                              <div
                                className={twMerge(
                                  'flex justify-center items-center h-full box-border rounded-[10px]'
                                )}>
                                <div
                                  className="w-full h-full rounded-[10px]"
                                  style={{
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'center',
                                    backgroundImage: `url(${PLACEHOLDER_LOGO})`,
                                    backgroundSize: '105px 31.5px'
                                  }}
                                />
                              </div>
                            }
                          />
                        </div>
                      )}
                    </ImpTracker>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default CarouselBanner;
